import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpenCheck,
  Check,
  CheckCircle2,
  Clock3,
  ExternalLink,
  FileText,
  Fingerprint,
  GraduationCap,
  LockKeyhole,
  MessageSquarePlus,
  ShieldCheck,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/hooks/useSession';
import { cn } from '@/lib/utils';
import { getControlledDocumentPreview } from '../api/eqmsApi';
import { TrainingSignatureDialog } from '../components/training/TrainingSignatureDialog';
import {
  TrainingComplianceBadge,
  TrainingStatusBadge,
} from '../components/training/TrainingStatusBadge';
import {
  useCompleteTraining,
  useSaveTrainingProgress,
  useSubmitTrainingQuiz,
  useTrainingAssignment,
} from '../hooks/useTraining';
import type {
  TrainingAssignment,
  TrainingQuizQuestion,
} from '../types/training.types';
import {
  canSignTraining,
  trainingProgress,
  trainingTimeRemaining,
} from '../utils/training';
import { MyUpdateRequests } from '../components/eqms/update-requests/MyUpdateRequests';
import { RequestUpdateDialog } from '../components/eqms/update-requests/RequestUpdateDialog';

function formatDate(value: string) {
  return new Intl.DateTimeFormat('en-CA', {
    dateStyle: 'long',
    timeZone: 'America/Montreal',
  }).format(new Date(value));
}

function EvidenceStep({
  label,
  detail,
  complete,
  active,
  locked,
}: {
  label: string;
  detail: string;
  complete: boolean;
  active: boolean;
  locked?: boolean;
}) {
  return (
    <div className="relative flex gap-3 pb-6 last:pb-0">
      <div className="absolute bottom-0 left-[15px] top-8 w-px bg-slate-200 last:hidden" />
      <div className={cn(
        'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border',
        complete && 'border-teal-500 bg-teal-500 text-white',
        active && !complete && 'border-amber-400 bg-amber-50 text-amber-700',
        !active && !complete && 'border-slate-200 bg-white text-slate-400',
        locked && 'border-rose-300 bg-rose-50 text-rose-700',
      )}>
        {locked ? <LockKeyhole className="h-3.5 w-3.5" /> : complete ? <Check className="h-4 w-4" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      </div>
      <div className="pt-0.5">
        <div className="text-sm font-semibold text-slate-950">{label}</div>
        <div className="mt-0.5 text-xs leading-5 text-slate-500">{detail}</div>
      </div>
    </div>
  );
}

function AssessmentDialog({
  open,
  questions,
  passingScore,
  attemptsRemaining,
  isPending,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  questions: TrainingQuizQuestion[];
  passingScore: number;
  attemptsRemaining: number;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (answers: Record<string, string>) => Promise<void>;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) setAnswers({});
  }, [open]);

  const complete = questions.every((question) => Boolean(answers[question.id]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Knowledge assessment</DialogTitle>
          <DialogDescription>
            Passing score {passingScore}% · {attemptsRemaining} attempt{attemptsRemaining === 1 ? '' : 's'} remaining.
            Submitting the final failed attempt locks qualification and alerts the assigned supervisor.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {questions.map((question, index) => (
            <fieldset key={question.id} className="space-y-3 rounded-xl border border-slate-200 p-4">
              <legend className="px-1 text-sm font-semibold text-slate-950">
                {index + 1}. {question.prompt}
              </legend>
              <div className="grid gap-2">
                {question.options.map((option) => {
                  const selected = answers[question.id] === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setAnswers((current) => ({ ...current, [question.id]: option.id }))}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors',
                        selected
                          ? 'border-teal-400 bg-teal-50 text-teal-950'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                      )}
                    >
                      <span className={cn(
                        'flex h-4 w-4 items-center justify-center rounded-full border',
                        selected ? 'border-teal-600 bg-teal-600 text-white' : 'border-slate-300',
                      )}>
                        {selected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                      </span>
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="bg-teal-700 text-white hover:bg-teal-800"
            disabled={!complete || isPending}
            onClick={() => onSubmit(answers)}
          >
            {isPending ? 'Scoring…' : 'Submit assessment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TrainingTaskContent({ assignment }: { assignment: TrainingAssignment }) {
  const { user } = useSession();
  const { t } = useTranslation('qms');
  const [requestUpdateOpen, setRequestUpdateOpen] = useState(false);
  const assignmentQuery = useTrainingAssignment(assignment.id);
  const saveProgressMutation = useSaveTrainingProgress(assignment.id);
  const quizMutation = useSubmitTrainingQuiz(assignment.id);
  const completeMutation = useCompleteTraining(assignment.id);
  const [readAttested, setReadAttested] = useState(Boolean(assignment.readAttestedAt));
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const current = assignmentQuery.data?.assignment || assignment;

  useEffect(() => {
    setReadAttested(Boolean(current.readAttestedAt));
  }, [current.readAttestedAt]);

  const previewQuery = useQuery({
    queryKey: ['eqms-training', 'assignment-document-preview', current.id, current.documentId, current.sourceVersionId ?? null],
    queryFn: () => getControlledDocumentPreview(current.documentId, current.sourceVersionId ?? undefined, current.id),
  });
  const isCompleted = current.status === 'completed';
  const isLocked = current.status === 'locked';
  const canStartAssessment = Boolean(current.readAttestedAt)
    && current.quizRequired
    && !current.quizPassedAt
    && !isLocked
    && !isCompleted;
  const detail = assignmentQuery.data;

  return (
    <>
      <div className="min-h-full bg-slate-50">
        <section className="border-b border-slate-800 bg-slate-950 text-white">
          <div className="mx-auto max-w-[1500px] px-4 py-6 sm:px-6 lg:px-8">
            <Link to="/eqms/training" className="inline-flex items-center text-sm text-slate-300 hover:text-white">
              <ArrowLeft className="mr-1 h-4 w-4" /> Training portal
            </Link>
            <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="border-teal-400/20 bg-teal-400/10 font-mono text-teal-200 hover:bg-teal-400/10">
                    {current.documentControlCode}
                  </Badge>
                  <TrainingStatusBadge status={current.status} />
                  <TrainingComplianceBadge state={current.complianceState} />
                </div>
                <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
                  {current.documentName}
                </h1>
                <p className="mt-2 text-sm text-slate-300">
                  Version {current.documentVersion} · {current.documentGroup} · assigned {formatDate(current.assignedAt)}
                </p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                <div className="text-xs uppercase tracking-wide text-slate-400">Qualification due</div>
                <div className="mt-1 font-medium">{formatDate(current.dueAt)}</div>
                <div className={cn(
                  'mt-1 text-xs',
                  current.complianceState === 'red' ? 'text-rose-300' : 'text-amber-200',
                )}>
                  {trainingTimeRemaining(current.dueAt)}
                </div>
              </div>
            </div>
          </div>
        </section>

        <main className="mx-auto grid max-w-[1500px] gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:px-8">
          <div className="space-y-5">
            {isCompleted && (
              <div className="flex items-start gap-3 rounded-xl border border-teal-200 bg-teal-50 p-4 text-teal-950">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-teal-700" />
                <div>
                  <div className="font-semibold">Qualification complete</div>
                  <div className="mt-1 text-sm text-teal-800">
                    Signed {current.completedAt ? new Date(current.completedAt).toISOString() : 'in UTC'}.
                    This training evidence is immutable.
                  </div>
                </div>
              </div>
            )}
            {isLocked && (
              <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-950">
                <LockKeyhole className="mt-0.5 h-5 w-5 text-rose-700" />
                <div>
                  <div className="font-semibold">Qualification locked</div>
                  <div className="mt-1 text-sm text-rose-800">
                    The maximum failed attempts were used. The assigned supervisor has been alerted.
                  </div>
                </div>
              </div>
            )}

            <Card className="overflow-hidden border-slate-200 shadow-sm">
              <CardHeader className="flex-row items-center justify-between border-b border-slate-100">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <FileText className="h-4 w-4 text-teal-700" /> Controlled document
                  </CardTitle>
                  <p className="mt-1 text-xs text-slate-500">
                    Read the controlled copy for this assignment before attesting.
                  </p>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setRequestUpdateOpen(true)}>
                    <MessageSquarePlus className="mr-1 h-3.5 w-3.5" /> {t('updateRequests.request')}
                  </Button>
                  {previewQuery.data?.previewUrl && (
                    <Button asChild variant="outline" size="sm">
                      <a href={previewQuery.data.previewUrl} target="_blank" rel="noreferrer">
                        Open <ExternalLink className="ml-1 h-3.5 w-3.5" />
                      </a>
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {previewQuery.isLoading && <Skeleton className="m-5 h-[560px]" />}
                {previewQuery.isError && (
                  <div className="flex h-64 items-center justify-center px-6 text-center text-sm text-slate-500">
                    The controlled copy could not be loaded. Refresh or contact Quality before attesting.
                  </div>
                )}
                {previewQuery.data?.previewUrl && (
                  <iframe
                    src={previewQuery.data.previewUrl}
                    title={`${current.documentControlCode} controlled document`}
                    className="h-[min(70vh,760px)] min-h-[520px] w-full border-0 bg-white"
                  />
                )}
              </CardContent>
            </Card>
            <MyUpdateRequests documentId={current.documentId} />
            <RequestUpdateDialog open={requestUpdateOpen} onOpenChange={setRequestUpdateOpen} documentId={current.documentId}
              documentLabel={`${current.documentControlCode} · ${current.documentName}`} />

            {!isCompleted && !isLocked && (
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="space-y-4 p-5">
                  <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-4 hover:bg-slate-50">
                    <Checkbox
                      checked={readAttested}
                      onCheckedChange={(checked) => setReadAttested(checked === true)}
                      disabled={Boolean(current.readAttestedAt)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block text-sm font-medium text-slate-950">
                        I have read and understood this controlled document.
                      </span>
                      <span className="mt-1 block text-xs leading-5 text-slate-500">
                        Saving this attestation creates attributable audit evidence and unlocks the assessment.
                      </span>
                    </span>
                  </label>
                  <div className="flex flex-wrap justify-end gap-2">
                    {!current.readAttestedAt && (
                      <Button
                        variant="outline"
                        disabled={!readAttested || saveProgressMutation.isPending}
                        onClick={() => saveProgressMutation.mutate()}
                      >
                        <BookOpenCheck className="mr-2 h-4 w-4" />
                        {saveProgressMutation.isPending ? 'Saving…' : 'Save reading evidence'}
                      </Button>
                    )}
                    {canStartAssessment && (
                      <Button
                        className="bg-amber-500 text-slate-950 hover:bg-amber-400"
                        onClick={() => setAssessmentOpen(true)}
                      >
                        <GraduationCap className="mr-2 h-4 w-4" /> Start assessment
                      </Button>
                    )}
                    {canSignTraining(current) && (
                      <Button
                        className="bg-teal-700 text-white hover:bg-teal-800"
                        onClick={() => setSignatureOpen(true)}
                      >
                        <Fingerprint className="mr-2 h-4 w-4" /> Sign qualification
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <aside className="space-y-5">
            <Card className="border-slate-200 shadow-sm lg:sticky lg:top-4">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base">Evidence path</CardTitle>
                <Progress value={trainingProgress(current)} className="mt-3 h-1.5" />
              </CardHeader>
              <CardContent className="p-5">
                <EvidenceStep
                  label="Read"
                  detail={current.readAttestedAt
                    ? `Attested ${new Date(current.readAttestedAt).toISOString()}`
                    : 'Review the assigned controlled copy and attest understanding.'}
                  complete={Boolean(current.readAttestedAt)}
                  active={!current.readAttestedAt && !isLocked}
                  locked={isLocked && !current.readAttestedAt}
                />
                <EvidenceStep
                  label="Assess"
                  detail={!current.quizRequired
                    ? 'Assessment not required by this matrix.'
                    : current.quizPassedAt
                      ? `Passed ${new Date(current.quizPassedAt).toISOString()}`
                      : `${current.attemptsRemaining} of ${current.maxAttempts} attempts remaining · ${current.passingScore}% pass`}
                  complete={!current.quizRequired || Boolean(current.quizPassedAt)}
                  active={Boolean(current.readAttestedAt) && !current.quizPassedAt && !isLocked}
                  locked={isLocked}
                />
                <EvidenceStep
                  label="Sign"
                  detail={current.completedAt
                    ? `Qualification manifested ${new Date(current.completedAt).toISOString()}`
                    : 'Reauthenticate and sign with the meaning “qualification”.'}
                  complete={isCompleted}
                  active={canSignTraining(current)}
                  locked={isLocked}
                />

                <div className="mt-6 border-t border-slate-100 pt-5">
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>Assessment attempts</span>
                    <span className="font-medium tabular-nums text-slate-800">
                      {current.attemptsUsed}/{current.maxAttempts}
                    </span>
                  </div>
                  {detail?.attempts.map((attempt) => (
                    <div key={attempt.id} className="mt-2 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs">
                      <span>Attempt {attempt.attemptNumber}</span>
                      <span className={attempt.passed ? 'font-medium text-teal-700' : 'font-medium text-rose-700'}>
                        {attempt.score}% · {attempt.passed ? 'Passed' : 'Failed'}
                      </span>
                    </div>
                  ))}
                </div>

                {detail?.signatures.map((signature) => (
                  <div key={signature.id} className="mt-5 rounded-xl border border-teal-100 bg-teal-50 p-3 text-xs text-teal-950">
                    <div className="flex items-center gap-2 font-semibold">
                      <ShieldCheck className="h-4 w-4" /> Electronic signature
                    </div>
                    <div className="mt-2">{signature.signerName} · {signature.meaning}</div>
                    <div className="mt-1 font-mono text-[11px] text-teal-800">
                      {new Date(signature.signedAt).toISOString()}
                    </div>
                    <div className="mt-2 text-teal-800">{signature.reason}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </aside>
        </main>
      </div>

      <AssessmentDialog
        open={assessmentOpen}
        questions={current.quizQuestions}
        passingScore={current.passingScore}
        attemptsRemaining={current.attemptsRemaining}
        isPending={quizMutation.isPending}
        onOpenChange={setAssessmentOpen}
        onSubmit={(answers) => quizMutation.mutateAsync(answers).then(() => setAssessmentOpen(false))}
      />

      <TrainingSignatureDialog
        open={signatureOpen}
        documentName={current.documentName}
        defaultUsername={user?.username || user?.email || ''}
        isPending={completeMutation.isPending}
        onOpenChange={setSignatureOpen}
        onConfirm={(input) => completeMutation.mutateAsync(input).then(() => setSignatureOpen(false))}
      />
    </>
  );
}

export function TrainingTaskPage() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const assignmentQuery = useTrainingAssignment(assignmentId || null);

  if (assignmentQuery.isLoading) {
    return (
      <div className="space-y-5 bg-slate-50 p-6">
        <Skeleton className="h-32 w-full" />
        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          <Skeleton className="h-[620px]" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (assignmentQuery.isError || !assignmentQuery.data?.assignment) {
    return (
      <div className="flex min-h-[55vh] items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-lg border-slate-200 text-center shadow-sm">
          <CardContent className="p-8">
            <Clock3 className="mx-auto h-9 w-9 text-slate-300" />
            <h1 className="mt-4 text-lg font-semibold">Training assignment unavailable</h1>
            <p className="mt-2 text-sm text-slate-500">
              It may not exist, or it belongs to another employee.
            </p>
            <Button asChild variant="outline" className="mt-5">
              <Link to="/eqms/training">Return to training portal</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <TrainingTaskContent assignment={assignmentQuery.data.assignment} />;
}
